import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  LoadingBar,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const Section = styled.div`
  border: 1px solid #e5eaf0;
  border-radius: 8px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
`;

const SectionTitle = styled(Typography)`
  font-weight: 700;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7a8d;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f5f5f5;
  color: #2c3e50;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ status }) => {
    if (status === "paid") return "#e6f4ea";
    if (status === "pending") return "#fff8e1";
    if (status === "failed") return "#fdecea";
    if (status === "requires_action") return "#fff8e1";
    return "#f5f5f5";
  }};
  color: ${({ status }) => {
    if (status === "paid") return "#1e7e34";
    if (status === "pending") return "#b45309";
    if (status === "failed") return "#c62828";
    if (status === "requires_action") return "#b45309";
    return "#666";
  }};
`;

const EmptyMsg = styled.div`
  padding: 24px;
  text-align: center;
  color: #8a9bb0;
  font-size: 13px;
`;

const fmt = (pence?: number) =>
  pence != null ? `£${(pence / 100).toFixed(2)}` : "—";

const fmtDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";

interface WalletFeeInvoiceRow {
  _id: string;
  invoiceNumber: string;
  status: string;
  periodStart: string;
  periodEnd: string;
  totalPence: number;
  currency: string;
  paidAt: string | null;
}

interface Props {
  companyId: string;
}

export const CompanyWalletFeeInvoicesTab: React.FC<Props> = ({ companyId }) => {
  const { showNotification } = useNotification();
  const [invoices, setInvoices] = useState<WalletFeeInvoiceRow[]>([]);
  const [loading, setLoading] = useState(true);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listCompanyWalletFeeInvoices(companyId, {
        limit: 20,
      });
      setInvoices(data?.docs ?? []);
    } catch (e) {
      showNotification(
        getApiErrorMessage(e, "Could not load wallet fee invoices"),
        NotificationTypes.WARNING
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, showNotification]);

  useEffect(() => {
    void loadInvoices();
  }, [loadInvoices]);

  return (
    <Wrap>
      <Section>
        <SectionTitle variant={TypographyVariant.HEADER3}>
          Wallet fee invoices
        </SectionTitle>
        {loading ? (
          <LoadingBar loading />
        ) : invoices.length === 0 ? (
          <EmptyMsg>No wallet fee invoices yet</EmptyMsg>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Period</Th>
                <Th>Status</Th>
                <Th>Total fees</Th>
                <Th>Paid at</Th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv._id}>
                  <Td style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {inv.invoiceNumber ?? inv._id}
                  </Td>
                  <Td>
                    {fmtDate(inv.periodStart)} – {fmtDate(inv.periodEnd)}
                  </Td>
                  <Td>
                    <StatusBadge status={inv.status}>{inv.status}</StatusBadge>
                  </Td>
                  <Td>{fmt(inv.totalPence)}</Td>
                  <Td>{fmtDate(inv.paidAt)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </Wrap>
  );
};

export default CompanyWalletFeeInvoicesTab;
